'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { FaCheck, FaAndroid } from 'react-icons/fa'
import { captureEmail } from '@/utils/captureEmail'
import { track } from '@/utils/analytics'

/**
 * InlineAndroidWaitlist — single-field, no-modal form.
 *
 * Why: GA4 + Supabase cross-reference shows 9 modal opens / 1 submit
 * (11% conversion) on the existing WaitlistModal. The modal pattern
 * itself is the friction — it requires a tap, a context switch, a
 * name field, then an email field, then a submit. For a hand-raise
 * waitlist where we just need an email, that's three taps too many.
 *
 * This component is the opposite shape: a single email input + submit
 * button rendered inline in a dedicated #android-waitlist section. No
 * modal, no name field, no rendering jumps. Mounted on the homepage
 * directly so visitors who clicked "Android waitlist" from the hero
 * or scan-demo land on it via smooth-scroll, and visitors who are
 * just browsing surface it organically as they scroll.
 *
 * Tracking:
 *  - `android_waitlist_open` fires once when the section enters
 *    viewport (so it counts toward the same funnel metric as the
 *    modal but reflects passive exposure too).
 *  - `android_waitlist_submit` (KEY EVENT) fires on success — same
 *    event the modal fires, so dashboards aggregate naturally.
 *  - `android_waitlist_error` on failure.
 *
 * Conversion goal: lift the 11% modal-conversion rate by removing
 * the modal step entirely. Even if the inline rate is only 25%, that
 * doubles waitlist signups at constant traffic.
 */
export interface InlineAndroidWaitlistProps {
  /** GA4 attribution. Defaults to `home_inline_waitlist`. */
  surface?: string
}

export default function InlineAndroidWaitlist({
  surface = 'home_inline_waitlist',
}: InlineAndroidWaitlistProps) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [hasFiredOpen, setHasFiredOpen] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || status === 'submitting') return

    setStatus('submitting')
    try {
      // Dual-write: durable list + the existing inbox notification. Succeeds
      // if either lands, so an EmailJS outage no longer loses the signup.
      const result = await captureEmail({
        email,
        list: 'android_waitlist',
        source: 'android_waitlist_inline',
        context: surface,
        emailParams: {
          from_name: '',
          reply_to: email,
          subject: '',
          message: '',
          type: 'android_waitlist',
        },
      })
      if (result.success) {
        setStatus('success')
        track('android_waitlist_submit', { cta_surface: surface })
        setEmail('')
      } else {
        setStatus('error')
        track('android_waitlist_error', { cta_surface: surface })
      }
    } catch {
      setStatus('error')
      track('android_waitlist_error', { cta_surface: surface })
    }
  }

  return (
    <section
      id="android-waitlist"
      aria-labelledby="android-waitlist-heading"
      className="relative w-full bg-white py-14 sm:py-section scroll-mt-24"
    >
      <div className="content">
        <motion.div
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-charcoal via-charcoal to-[#1f2937] p-6 sm:p-8 md:p-10 shadow-2xl shadow-charcoal/20"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.55 }}
          onViewportEnter={() => {
            if (!hasFiredOpen) {
              setHasFiredOpen(true)
              track('android_waitlist_open', { cta_surface: surface })
            }
          }}
        >
          {/* Decorative Android-green accent. Pure visual — aria-hidden. */}
          <div
            aria-hidden="true"
            className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#3DDC84]/15 blur-3xl"
          />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center">
            <div className="md:col-span-6">
              <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#3DDC84] mb-3">
                <FaAndroid className="w-3.5 h-3.5" aria-hidden="true" />
                Android · early access
              </p>
              <h2
                id="android-waitlist-heading"
                className="text-[clamp(1.5rem,4vw,2rem)] font-bold text-white leading-[1.15] mb-3"
              >
                Get the launch email the day Android opens.
              </h2>
              <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                One email. No spam. We send when the Android app is in the
                Play Store, and never again unless you opt in.
              </p>
            </div>

            <div className="md:col-span-6">
              {status === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-3 bg-[#3DDC84]/15 border border-[#3DDC84]/40 rounded-xl px-4 py-3.5 text-white"
                  role="status"
                >
                  <FaCheck className="w-5 h-5 text-[#3DDC84]" aria-hidden="true" />
                  <span className="text-sm font-semibold">You&apos;re on the list. We&apos;ll email you on launch day.</span>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 sm:items-stretch">
                  <label htmlFor="android-waitlist-email" className="sr-only">
                    Email address for Android waitlist
                  </label>
                  <input
                    id="android-waitlist-email"
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="flex-1 min-w-0 sm:min-w-[14rem] bg-white/10 hover:bg-white/15 focus:bg-white/15 text-white placeholder:text-white/40 border border-white/20 focus:border-[#3DDC84] focus:ring-2 focus:ring-[#3DDC84]/40 rounded-control px-4 py-3.5 text-base outline-none transition-[color,background-color,border-color,text-decoration-color] pressable"
                  />
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex items-center justify-center bg-[#3DDC84] hover:bg-[#3DDC84]/90 text-charcoal font-semibold px-5 py-3.5 rounded-control transition-[color,background-color,border-color,text-decoration-color] duration-200 min-h-[48px] shadow-lg shadow-[#3DDC84]/30 disabled:opacity-60 disabled:cursor-not-allowed pressable"
                  >
                    {status === 'submitting' ? 'Joining…' : 'Notify me'}
                  </button>
                </form>
              )}
              {status === 'error' && (
                <p className="mt-2 text-sm text-red-300" role="alert">
                  Couldn&apos;t join right now. Try again in a moment.
                </p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
